import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import PageBreadcrumb from '../../../../Components/Common/PageBreadcrumb';
import classnames from 'classnames';
import RequestHpsMarket from './RequestHpsMarket';
import InviteDmut from './InviteDmut';
import InvitePanitiaTender from './InvitePanitiaTender';
import RequestFreightAvailability from './RequestFreightAvailability';
import useProcurementDetail from '../../hooks/useProcurementDetail';
import useProcurementDocuments from '../../hooks/useProcurementDocuments';
import { useQuantityDeliveries } from '../../../../Components/QuantityTable';
import {
  displayValue,
  formatPeriodRange,
  formatSubmittedDate,
  getContractTypeLabel,
  getProcurementTypeLabel,
  getTenderOpeningStatusConfig,
  getTenderOpeningTabCompletion,
  formatProcurementProductTypeNames,
} from '../../utils/tenderOpeningHelpers';
import {
  DetailCard,
  DetailHeader,
  MetaList,
  ProgressChecklist,
} from '../../../../Components/Common/Detail';
import { formatDateToddmmyyhhmm } from '../../../../utils/dateformatter';
import { Skeleton } from '../../../../Components/Common/Skeleton';
import StatusBadge from '../../../../Components/Common/StatusBadge';

const DETAIL_TABS = [
  {
    id: 'request-hps',
    label: 'Request HPS Market',
    icon: 'ri-file-list-3-line',
    component: RequestHpsMarket,
    completionKey: 'isRequestHpsCompleted',
  },
  {
    id: 'invite-dmut',
    label: 'Invite Registered Supplier',
    icon: 'ri-mail-send-line',
    component: InviteDmut,
    completionKey: 'isInviteDMUTCompleted',
  },
  {
    id: 'invite-panitia',
    label: 'Invite Panitia Tender',
    icon: 'ri-group-line',
    component: InvitePanitiaTender,
    completionKey: 'isInvitePanitiaTenderCompleted',
    outsideProgress: true,
  },
  {
    id: 'req-freight',
    label: 'Request Freight Availability',
    icon: 'ri-ship-line',
    component: RequestFreightAvailability,
    completionKey: 'isReqFreightAvailabilityCompleted',
  },
];

const TenderOpeningTabStatusIcon = ({ completionFlag }) => {
  if (completionFlag === true) {
    return (
      <i
        className='ri-checkbox-circle-line text-success ms-2 align-middle'
        title='Done'
        aria-label='Done'
      />
    );
  }

  if (completionFlag === false) {
    return (
      // <i
      //   className='ri-error-warning-line text-warning ms-2 align-middle'
      //   title='In Progress'
      //   aria-label='In Progress'
      // />
      <i
        className='ri-error-warning-line text-warning ms-2 align-middle fs-20'
        title='Open'
        aria-label='Open'
      />
    );
  }

  return (
    <i
      className='ri-checkbox-blank-circle-fill text-secondary ms-2 align-middle'
      title='Open'
      aria-label='Open'
    />
  );
};

const DetailProductHydro = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('request-hps');
  const { procurement, isLoading, refresh } = useProcurementDetail(id);
  const { documents: procurementDocuments, isLoading: isLoadingDocuments } =
    useProcurementDocuments(id);
  const { parcels, isLoading: isLoadingParcels } = useQuantityDeliveries(id);

  useEffect(() => () => toast.dismiss(), []);

  const visibleTabs = (() => {
    const filtered = DETAIL_TABS.filter(tab => {
      if (tab.id === 'req-freight')
        return procurement?.isReqFreightAvailabilityEnable;
      return true;
    });
    const progressTabs = filtered.filter(tab => !tab.outsideProgress);
    const outsideTabs = filtered.filter(tab => tab.outsideProgress);
    const freightTab = progressTabs.find(tab => tab.id === 'req-freight');
    const otherProgress = progressTabs.filter(tab => tab.id !== 'req-freight');
    if (freightTab) {
      return [...otherProgress, freightTab, ...outsideTabs];
    }
    return [...otherProgress, ...outsideTabs];
  })();

  const statusConfig = getTenderOpeningStatusConfig(
    procurement?.tenderOpeningStatus
  );
  const categoryLabel =
    procurement?.procurementTypeHeaderLabel || 'Product Hydro';

  const completionSteps = visibleTabs
    .filter(tab => !tab.outsideProgress)
    .map(tab => ({
      id: tab.id,
      label: tab.label,
      done: getTenderOpeningTabCompletion(procurement, tab.completionKey),
    }));
  const panitiaSent =
    getTenderOpeningTabCompletion(
      procurement,
      'isInvitePanitiaTenderCompleted'
    ) === true;

  const tabContent = (
    <div className='dt-tabs'>
      <Nav tabs className='nav-tabs-custom nav-primary'>
        {visibleTabs.map(tab => {
          const completionFlag = getTenderOpeningTabCompletion(
            procurement,
            tab.completionKey
          );

          return (
            <NavItem key={tab.id}>
              <NavLink
                tag='button'
                type='button'
                className={classnames('cursor-pointer', {
                  active: activeTab === tab.id,
                })}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`${tab.icon} align-middle me-1`}></i>
                <span className='fw-semibold d-inline-flex align-items-center'>
                  {tab.label}
                  {procurement && (
                    <TenderOpeningTabStatusIcon
                      completionFlag={completionFlag}
                    />
                  )}
                </span>
              </NavLink>
            </NavItem>
          );
        })}
      </Nav>

      <TabContent activeTab={activeTab} className='p-3 p-md-4'>
        {visibleTabs.map(tab => {
          if (activeTab !== tab.id) return null;

          const TabComponent = tab.component;
          const sharedProps = {
            procId: id,
            procurement,
            isLoadingProcurement: isLoading,
            onRefreshProcurement: refresh,
            parcels,
            isLoadingParcels,
          };
          const inviteProps = {
            procurementDocuments,
            isLoadingDocuments,
          };

          return (
            <TabPane key={tab.id} tabId={tab.id}>
              <TabComponent
                {...sharedProps}
                {...(tab.id === 'invite-dmut' || tab.id === 'invite-panitia'
                  ? inviteProps
                  : {})}
              />
            </TabPane>
          );
        })}
      </TabContent>
    </div>
  );

  return (
    <div className='page-content'>
      <Container fluid>
        <PageBreadcrumb
          fallback={{
            label: 'Tender Opening',
            to: '/apps-tenderopening/view',
          }}
          current={`Detail # ${displayValue(procurement?.procurementNumber)}`}
          listClassName='p-3 py-2 bg-light'
        />

        <DetailHeader
          loading={isLoading && !procurement}
          onBack={() => navigate('/apps-tenderopening/view')}
          backLabel='Back to list'
          eyebrow={
            <>
              <i className='ri-hashtag' aria-hidden='true' />
              <span>{displayValue(procurement?.procurementNumber)}</span>
              <span className='opacity-50'>|</span>
              <span>{categoryLabel}</span>
            </>
          }
          title={displayValue(procurement?.titleProcurement)}
          badges={
            <StatusBadge tone={statusConfig.tone} label={statusConfig.label} />
          }
          facts={[
            {
              icon: 'ri-calendar-event-line',
              label: 'Submitted',
              value: formatSubmittedDate(
                procurement?.submittedDateTenderOpening
              ),
            },
            {
              icon: 'ri-repeat-line',
              label: 'Contract',
              value: getContractTypeLabel(procurement?.contractType),
            },
            {
              icon: 'ri-calendar-2-line',
              label: 'Period',
              value: formatPeriodRange(procurement),
            },
            {
              icon: 'ri-time-line',
              label: 'Proposal deadline',
              value: procurement?.proposalSubmissionDeadline
                ? formatDateToddmmyyhhmm(procurement.proposalSubmissionDeadline)
                : null,
            },
          ]}
        />

        <Row>
          <Col xl={9} className='order-2 order-xl-1'>
            {tabContent}
          </Col>
          <Col xl={3} className='order-1 order-xl-2'>
            <DetailCard
              title='Tender progress'
              icon='ri-task-line'
              hint='Select a step to open its tab'
            >
              {isLoading && !procurement ? (
                <Skeleton width='100%' height={120} />
              ) : (
                <>
                  <ProgressChecklist
                    steps={completionSteps}
                    activeId={activeTab}
                    onSelect={setActiveTab}
                  />
                  <div className='d-flex align-items-stretch gap-2 mt-3'>
                    <span className='border-start' aria-hidden='true' />
                    <ul className='dt-steps flex-grow-1'>
                      <li>
                        <button
                          type='button'
                          className={classnames('dt-step', {
                            'is-done': panitiaSent,
                            'is-open': !panitiaSent,
                            'is-active': activeTab === 'invite-panitia',
                          })}
                          onClick={() => setActiveTab('invite-panitia')}
                        >
                          <i
                            className={`dt-step__icon ${
                              panitiaSent
                                ? 'ri-checkbox-circle-fill'
                                : 'ri-government-line'
                            }`}
                            aria-hidden='true'
                          />
                          <span className='dt-step__label'>
                            Invite Panitia Tender
                          </span>
                          <span className='dt-step__state'>
                            {panitiaSent ? 'Sent' : 'Not sent'}
                          </span>
                        </button>
                      </li>
                    </ul>
                  </div>
                </>
              )}
            </DetailCard>

            <DetailCard title='Procurement info' icon='ri-information-line'>
              <MetaList
                stacked
                items={[
                  {
                    label: 'Product',
                    value: procurement?.productType?.product?.name,
                  },
                  {
                    label: 'Product type',
                    value: formatProcurementProductTypeNames(procurement),
                  },
                  {
                    label: 'Procurement method',
                    value: getProcurementTypeLabel(
                      procurement?.procurementType
                    ),
                  },
                  {
                    label: 'Payment currency',
                    value: procurement?.paymentCurrency?.name,
                  },
                  { label: 'Governing law', value: procurement?.law?.name },
                  {
                    label: 'Arbitration',
                    value: procurement?.arbitration?.name,
                  },
                ]}
              />
            </DetailCard>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DetailProductHydro;
